import { type AnnouncementItemEditorState } from '@components/announcement/util/announcementItemEditorState.util';
import { type AnnouncementItemContentRequest } from '@model/announcement/AnnouncementItemContentRequest';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { type AnnouncementRichTextContentDTO, type ColorDTO } from '@/model';

const PREVIEW_DEBOUNCE_MS = 1000;

interface UseAnnouncementPreviewRequestProps {
  editorState: AnnouncementItemEditorState;
  backgroundColor: ColorDTO;
  richTextContent: AnnouncementRichTextContentDTO;
  blackAndWhite: boolean;
  automaticPreview: boolean;
}

interface UseAnnouncementPreviewRequestResult {
  // The request to actually key the preview query on — debounced while
  // automatic preview is on, immediate otherwise (a manual refetch should
  // never send a stale, still-debouncing value).
  previewRequest: AnnouncementItemContentRequest;
  isPreviewRequestPending: boolean;
}

export const useAnnouncementPreviewRequest = ({
  editorState,
  backgroundColor,
  richTextContent,
  blackAndWhite,
  automaticPreview,
}: UseAnnouncementPreviewRequestProps): UseAnnouncementPreviewRequestResult => {
  // Memoized so the reference is stable across renders that don't actually
  // change any of these fields — otherwise a brand new object on every render
  // would make the useDebounce below think the value keeps changing, never
  // settling and always reporting a pending debounce.
  const rawPreviewRequest = useMemo(
    () => ({
      file: editorState.file,
      imageId: editorState.imageId,
      width: editorState.width,
      height: editorState.height,
      anchorX: editorState.backgroundHorizontalAlign,
      anchorY: editorState.backgroundVerticalAlign,
      backgroundColor,
      imageResizeType: editorState.imageResizeType,
      richTextContent,
      offsetX: editorState.x,
      offsetY: editorState.y,
      blackAndWhite,
    }),
    [
      editorState.file,
      editorState.imageId,
      editorState.width,
      editorState.height,
      editorState.backgroundHorizontalAlign,
      editorState.backgroundVerticalAlign,
      backgroundColor,
      editorState.imageResizeType,
      richTextContent,
      editorState.x,
      editorState.y,
      blackAndWhite,
    ],
  );

  const [debouncedPreviewRequest, debouncedPreviewRequestControls] = useDebounce(
    rawPreviewRequest,
    PREVIEW_DEBOUNCE_MS,
  );

  return {
    previewRequest: automaticPreview ? debouncedPreviewRequest : rawPreviewRequest,
    isPreviewRequestPending:
      automaticPreview && debouncedPreviewRequestControls.isPending(),
  };
};
